package com.vamvisions.main.pages.document

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.vamvisions.main.components.ErrorView
import com.vamvisions.main.components.LoadingView
import com.vamvisions.main.components.markdown.MarkdownContent
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.i18n.resolve
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.di.koinViewModel
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.layout.PageLayout
import com.vamvisions.main.pages.NotFoundContent
import com.vamvisions.main.portfolio.presentation.document.DocumentAction
import com.vamvisions.main.portfolio.presentation.document.DocumentState
import com.vamvisions.main.portfolio.presentation.document.DocumentViewModel
import org.koin.core.parameter.parametersOf

/**
 * A project document, such as a privacy policy, in the language from the URL. The interface language
 * doesn't change which document is shown; switching it moves to the same document in the new
 * language, in place of the current history entry.
 */
@Composable
fun DocumentRoot(route: Route.ProjectDocument, chrome: PageChrome) {
    val viewModel = koinViewModel<DocumentViewModel> { parametersOf(route.slug, route.language) }
    val state by viewModel.state.collectAsState()
    val strings = LocalStrings.current
    val onLanguageSwitch = remember(route.slug) { { language: Language -> Route.ProjectDocument(route.slug, language) } }
    PageLayout(
        title = when {
            state.notFound -> strings.notFoundTitle
            else -> state.title ?: strings.loading
        },
        chrome = chrome,
        onLanguageSwitch = onLanguageSwitch,
    ) {
        DocumentScreen(
            state = state,
            language = route.language,
            onAction = viewModel::onAction,
            onNavigate = chrome.onNavigate,
        )
    }
}

@Composable
fun DocumentScreen(
    state: DocumentState,
    language: Language,
    onAction: (DocumentAction) -> Unit,
    onNavigate: (Route) -> Unit,
) {
    val error = state.error
    when {
        state.notFound -> NotFoundContent(onNavigate)
        error != null -> ErrorView(error.resolve(LocalStrings.current)) { onAction(DocumentAction.Retry) }
        state.isLoading -> LoadingView()
        else -> MarkdownContent(state.content, language, onNavigate)
    }
}
